/**
 * What an upcoming meeting's row offers and how it is labelled — the pure half of the Meetings channel.
 *
 * Import-free on purpose. `lib/meetings-channel.ts` reaches react-query and `api-client`, and a jest
 * test importing that chain resolves common-lib's ESM-only dependencies — which passes locally against
 * `dist/` and fails the whole suite in CI, where it resolves to source. Same reason
 * `lib/recipient-error.ts` and `lib/brief-date-key.ts` exist. Re-exported from `meetings-channel.ts`,
 * so no call site has to know.
 */

export type UpcomingMeetingState = 'scheduled' | 'not_scheduled' | 'skipped' | 'paused' | 'in_meeting' | 'failed';

/** What a row offers the user, derived from its state. `none` is a row the user cannot change. */
export type MeetingRowAction = 'join' | 'skip' | 'undo' | 'none';

export function meetingRowAction(state: UpcomingMeetingState): MeetingRowAction {
  if (state === 'scheduled') return 'skip';
  if (state === 'not_scheduled') return 'join';
  if (state === 'skipped') return 'undo';
  // `in_meeting` and `failed` are already decided — a meeting she has been sent to already has its
  // session, so a Join would arm nothing; `paused` is the site's server-side switch being off, which a
  // Join here could not turn on — a button that quietly does nothing is worse than no button.
  return 'none';
}

/** The decision a row's action records. `null` clears it back to whatever the connection says. */
export function meetingRowDecision(action: MeetingRowAction): 'join' | 'skip' | null {
  if (action === 'join') return 'join';
  if (action === 'skip') return 'skip';
  return null;
}

/**
 * The DS Badge style each state wears. Named styles, never colours — the badge resolves its own
 * `--ds-badge-*` triple, which is what keeps a meeting's state reading like every other status in
 * the app instead of like a pill somebody drew here.
 *
 * Spelled as a literal union rather than `string`, so a typo is a compile error here instead of an
 * unstyled badge at runtime: `Badge` falls back to its default variant for a name it does not know,
 * and the call site can assign this straight across with no cast. The five names are a subset of
 * `BadgeVariant` in `components/symphony/ui/Badge.tsx` — which this module deliberately does not
 * import, because a UI import from here would drag the graph a node-env test cannot load.
 */
type MeetingStateBadge = 'connected' | 'waiting-on-a-reply' | 'paused' | 'in-progress' | 'failed';

export const MEETING_STATE_BADGE: Record<UpcomingMeetingState, MeetingStateBadge> = {
  scheduled: 'connected',
  not_scheduled: 'waiting-on-a-reply',
  skipped: 'paused',
  paused: 'paused',
  in_meeting: 'in-progress',
  failed: 'failed',
};

/**
 * A row's identity in the list.
 *
 * **Not the event id alone.** The list is assembled by walking every connected calendar and appending
 * its events, so one underlying meeting reaches it once per calendar that holds it — and the provider
 * hands the copies the same event id. Keyed by the event id, two such rows collide: React reconciles
 * one onto the other after a refetch, so a row can end up wearing a neighbour's state.
 *
 * `dedupeMeetingsByEvent` below is what makes that collision rare rather than routine, but the
 * composite stays the key: it is the only value in the row guaranteed distinct even mid-dedupe, and a
 * list whose keys are correct only because of an upstream filter is one refactor from the bug.
 */
export function meetingRowKey(meeting: { calendarTokenId: string; calendarId: string; calendarEventId: string }): string {
  return `${meeting.calendarTokenId}/${meeting.calendarId}/${meeting.calendarEventId}`;
}

/**
 * One row per MEETING, not per calendar copy of it.
 *
 * The decision a row records is stored per `(site, event)` upstream — `meetingJoinRequestKey` hashes
 * the metasite and the calendar event id and nothing else, and the join claim adds only the start
 * time. The calendar and token in the request body pick which calendar the event is READ from when
 * the capsule is armed; they are not part of the answer's identity.
 *
 * So two rows for the same event, reached through two connected calendars, are not two decisions —
 * they are one, rendered twice. Left in, a Skip on either makes both say Skipped on the next refetch,
 * and an Undo on either clears both: two controls that look independent and are not. The first copy
 * wins, which is the calendar the upstream walked first, and its token is the one any arming will
 * read through.
 */
export function dedupeMeetingsByEvent<T extends { calendarEventId: string }>(meetings: readonly T[]): T[] {
  const seen = new Set<string>();
  return meetings.filter((meeting) => {
    if (seen.has(meeting.calendarEventId)) return false;
    seen.add(meeting.calendarEventId);
    return true;
  });
}

/**
 * The meeting the Next up card shows, and the rest of the week for the list under it.
 *
 * `next` is the first meeting that is not `over` — by default one her session has not already dropped out of: a
 * `failed` one earlier in the list stays in `rest` rather than being promoted over the call that is still ahead.
 * Web passes `meetingIsOver`, which also counts a phase 1 row that has ended (`lib/meeting-view.ts`). Mirrored on both
 * phones with the same test names; the three surfaces must agree on which meeting is "next".
 */
export function splitNextMeeting<T extends { state: UpcomingMeetingState }>(
  meetings: readonly T[],
  over: (meeting: T) => boolean = (meeting) => meeting.state === 'failed',
): { next: T | null; rest: T[] } {
  const index = meetings.findIndex((meeting) => !over(meeting));
  if (index < 0) return { next: null, rest: [...meetings] };
  return { next: meetings[index]!, rest: meetings.filter((_, i) => i !== index) };
}

/**
 * The meetings that have not ended by `now`. The server leaves ended ones out, but the view stays open
 * past a meeting's end, and a meeting that is over belongs to Past meetings. An unknown end never ends.
 */
export function meetingsNotEnded<T extends { endsAt?: string }>(meetings: readonly T[], now: number): T[] {
  return meetings.filter((meeting) => {
    const endsAt = meeting.endsAt ? Date.parse(meeting.endsAt) : NaN;
    return !(Number.isFinite(endsAt) && endsAt <= now);
  });
}

/**
 * Consecutive items that fall on the same local day, keyed by that day's midnight. The input order is
 * kept, so a list sorted by time comes out as its days in the same order. Both tabs group this way.
 */
export function groupByDay<T>(items: readonly T[], at: (item: T) => number): { day: number; items: T[] }[] {
  const groups: { day: number; items: T[] }[] = [];
  for (const item of items) {
    const midnight = new Date(at(item));
    midnight.setHours(0, 0, 0, 0);
    const day = midnight.getTime();
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(item);
    else groups.push({ day, items: [item] });
  }
  return groups;
}
